import { useEffect, useRef, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { useToast } from "../../context/ToastContext";
import { Button } from "../common/Button";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { SelectOrAdd } from "../common/SelectOrAdd";
import { ProgressBar } from "../common/ProgressBar";
import { Modal } from "../common/Modal";
import { BatchTreatmentDialog } from "./BatchTreatmentDialog";
import { BulkUpdateDialog } from "./BulkUpdateDialog";
import { TeamAssignDialog } from "./TeamAssignDialog";
import { BulkFeedingDialog } from "./BulkFeedingDialog";
import { BatchSeasonDialog } from "./BatchSeasonDialog";
import styles from "./BatchActionBar.module.css";

// Actions that open their own dedicated dialog (for parameter entry) rather
// than going through the generic ConfirmDialog + run() flow below.
// Actions that ASK something before they run. Apply opens the form for
// these and the confirm step for everything else, so an action with a
// dialog that is missing from this list silently gets the confirm
// instead -- it applies, with whatever its form would have collected
// left empty. close_season needs a date, so it belongs here;
// open_season takes no input and deliberately does not.
const MODAL_ACTION_TYPES = ["treatment", "bulk_update", "bulk_feeding", "close_season"];

/**
 * Selection Mode toolbar: shown when 1+ rows are selected. Available
 * actions depend on theme -- "Mark Checked", "Archive", and "Bulk Update"
 * are Basic; "Apply Treatment" needs the Treatments feature and "Bulk
 * Feeding" needs the Feedings feature (Advanced/Custom).
 * Also supports saving the current action as a reusable template and
 * one-click applying a previously saved one (GET/POST /hives/batch-template*).
 */
export function BatchActionBar({ selection, onDone, canArchive = true, canRestore = false }) {
  const { hasFeature } = useFeatureTheme();
  const { notify } = useToast();
  const [action, setAction] = useState("mark_checked");
  const [teamAssignOpen, setTeamAssignOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Only read for an archive; cleared by the dialog closing on its own.
  const [archiveReason, setArchiveReason] = useState("");
  const [progress, setProgress] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [treatmentOpen, setTreatmentOpen] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkFeedingOpen, setBulkFeedingOpen] = useState(false);
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [pendingParams, setPendingParams] = useState(null);
  // The finished progress bar lingers briefly before clearing. If the bar
  // unmounts inside that window (selection cleared, route changed) the timer
  // would still fire and set state on a gone component, so keep the id.
  const progressTimerRef = useRef(null);

  // Every /hives/batch-* endpoint is gated on BatchOperations, but two of
  // these actions have ungated single-hive equivalents (DELETE /hives/{id}
  // and POST /hives/{id}/action). Reported from the field: a Basic user
  // selecting one hive and choosing Archive got "'BatchOperations' is not
  // enabled for your current theme" -- told to upgrade for something that
  // does not actually require it. Below, a single selection takes the
  // single-hive route; only genuinely multi-hive work needs the feature.
  const canBatch = hasFeature("BatchOperations");
  const single = selection.count === 1;

  const actions = [
    { value: "mark_checked", label: "Mark Checked" },
    /*
     * RESTORE IN THE ARCHIVED VIEW, ARCHIVE EVERYWHERE ELSE.
     *
     * Reported on the device round of 20 Sep: "when I'm viewing archived
     * hives I can multi-select but there is no batch 'restore' option in
     * the dropdown." The selection already worked; there was nowhere for
     * it to go.
     *
     * The asymmetry was the bug. Batch archive shipped with batch
     * operations and batch restore never did, so a mis-aimed selection
     * could archive eleven hives in one press and then needed eleven
     * separate restores to undo -- the easy direction being the
     * destructive one, which is backwards.
     *
     * DERIVED FROM THE SELECTION, not from the status filter, which is
     * what the first version did. Reported from a device: under "All",
     * selecting archived AND active hives together offered Archive but
     * not Restore -- because the filter was standing in for what was
     * actually picked, and under "All" the filter says nothing about
     * either.
     *
     * A mixed selection offers BOTH, and that is safe in both
     * directions: batch archive sets is_archived = 1 on rows that
     * already have it, which changes nothing, and batch restore carries
     * `AND is_archived = 1`, so it touches only the archived ones and
     * the count it reports stays honest.
     *
     * A pure selection still offers exactly one. An action that
     * silently does nothing is worse than one that is absent: the list
     * does not change and the user cannot tell whether it worked.
     */
    ...(canArchive ? [{ value: "archive", label: "Archive" }] : []),
    ...(canRestore ? [{ value: "restore", label: "Restore" }] : []),
    /*
     * UNGATED, deliberately (#389).
     *
     * Every /hives/batch-* endpoint needs BatchOperations, and POST
     * /hives/season is not one of them -- it takes a list because a truck
     * takes a yard, not because bulk is the capability being sold.
     *
     * Shutting hives for winter is ordinary beekeeping that a keeper with
     * six hives does as surely as one with six hundred. Gating it would
     * recreate the complaint this file already carries: a Basic user
     * "told to upgrade for something that does not actually require it".
     */
    //
    // OFFERED ONLY WHILE SOMETHING IN THE SELECTION IS IN SERVICE.
    // An archived hive is out of the rotation, so closing its season
    // implies it was still in one -- HiveDetailPage has gated this on
    // status since #389 and the batch bar never did. Found when the
    // mobile version of the same gap was reported as an "accidental
    // successful test"; a rule living on one platform is half a rule
    // (#325). canArchive is exactly "at least one selected hive is not
    // archived", so a mixed selection still gets these.
    ...(canArchive ? [
      { value: "close_season", label: "Close for the Season" },
      { value: "open_season", label: "Open for the Season" },
    ] : []),
    // No single-hive equivalent worth special-casing: the dialog exists to
    // apply one change across many hives, which is the gated capability.
    ...(canBatch ? [{ value: "bulk_update", label: "Bulk Update" }] : []),
    ...(hasFeature("Treatments") && canBatch ? [{ value: "treatment", label: "Apply Treatment" }] : []),
    ...(hasFeature("Feedings") && canBatch ? [{ value: "bulk_feeding", label: "Bulk Feeding" }] : []),
    // Handing hives to somebody (#288). Suggested from the field: check
    // the hives that need work, then choose who does them -- which is
    // where a person already is when they decide.
    //
    // Gated on TeamCollaboration, not BatchOperations: assigning one hive
    // to a teammate is the plain team feature, and there is no bulk
    // capability being sold here beyond saving repeated clicks.
    ...(hasFeature("TeamCollaboration") ? [{ value: "team_assign", label: "Assign to Team Member" }] : []),
  ];

  /*
   * A CHOSEN ACTION THAT IS NO LONGER OFFERED MUST NOT STAY CHOSEN.
   *
   * Archive and Restore appear and disappear as the SELECTION changes.
   * Without this, choosing Archive and then selecting only archived
   * hives leaves `action` on "archive" while the <select> has no such
   * option -- so the control renders blank and Apply runs something the
   * user can no longer see they chose. The same shape as a feature being
   * turned off mid-session, which is why this checks membership rather
   * than watching any one flag.
   */
  const offered = actions.map((a) => a.value).join(",");
  useEffect(() => {
    // `offered` rather than `actions`: the array is rebuilt every render,
    // so depending on it would re-run this on every render. The string
    // only changes when the set of choices actually does.
    if (!offered.split(",").includes(action)) setAction("mark_checked");
  }, [offered, action]);

  function openModalFor(actionType, params) {
    setPendingParams(params || null);
    if (actionType === "close_season") setSeasonOpen(true);
    else if (actionType === "team_assign") setTeamAssignOpen(true);
    else if (actionType === "treatment") setTreatmentOpen(true);
    else if (actionType === "bulk_update") setBulkUpdateOpen(true);
    else if (actionType === "bulk_feeding") setBulkFeedingOpen(true);
  }

  function handleDialogApplied() {
    selection.clear();
    onDone?.();
  }

  useEffect(() => {
    // /hives/batch-templates is gated too, so this 403'd on every render for
    // Basic users. The .catch swallowed it, which is why nobody noticed.
    if (!canBatch) return;
    hivesApi.listBatchTemplates().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, [canBatch]);

  useEffect(() => () => clearTimeout(progressTimerRef.current), []);

  if (selection.count === 0) return null;

  async function run() {
    setConfirming(false);
    setProgress({ done: 0, total: selection.count });
    try {
      if (action === "mark_checked") {
        // 'Inspection' matches what batch_inspection writes to
        // app.HiveActions, so one hive and many produce the same history.
        if (single) await hivesApi.logAction(selection.ids[0], { action_type: "Inspection", details: {} });
        else await hivesApi.batchInspection({ hive_ids: selection.ids });
      } else if (action === "open_season") {
        /*
         * No dialog: opening takes no input. Closing needs a date and so
         * gets a form; putting one in front of this would be asking a
         * question with one answer.
         *
         * A batch of one goes down the SAME route, unlike archive above --
         * there is no single-hive season endpoint to fall back to, and
         * inventing one would give closing two implementations to drift
         * apart. POST /hives/season is ungated, so nothing is lost by it.
         */
        await hivesApi.setSeason(selection.ids, null);
      } else if (action === "archive") {
        // DELETE /hives/{id} is a soft archive (is_archived = 1,
        // status = 'Archived'), the same end state as batch-archive.
        if (single) await hivesApi.remove(selection.ids[0], { reason: archiveReason });
        else await hivesApi.batchArchive(selection.ids, { reason: archiveReason });
      } else if (action === "restore") {
        // Same single/batch split as archive, and for the same reason:
        // POST /hives/{id}/restore is ungated, so restoring one hive
        // must not tell a Basic user to upgrade for BatchOperations --
        // which is the exact complaint this file already carries.
        if (single) await hivesApi.restore(selection.ids[0]);
        else await hivesApi.batchRestore(selection.ids);
      }
      setProgress({ done: selection.count, total: selection.count });
      notify(`${actions.find((a) => a.value === action).label} applied to ${selection.count} hive(s)`, "success");
      selection.clear();
      onDone?.();
    } catch (err) {
      notify(err.apiMessage || "Batch action failed", "danger");
    } finally {
      clearTimeout(progressTimerRef.current);
      progressTimerRef.current = setTimeout(() => setProgress(null), 800);
    }
  }

  async function saveTemplate() {
    if (!templateName.trim()) return notify("Template name is required", "warning");
    try {
      await hivesApi.saveBatchTemplate({ template_name: templateName.trim(), operation_type: action, parameters: {} });
      notify(`Template "${templateName.trim()}" saved`, "success");
      setSaveOpen(false);
      setTemplateName("");
      const r = await hivesApi.listBatchTemplates();
      setTemplates(r.templates);
    } catch (err) {
      notify(err.apiMessage || "Could not save template", "danger");
    }
  }

  function applyTemplate(templateId) {
    const template = templates.find((t) => String(t.template_id) === String(templateId));
    if (!template) return;
    if (!actions.some((a) => a.value === template.operation_type)) {
      notify(`"${template.operation_type}" isn't available with your current theme`, "warning");
      return;
    }
    setAction(template.operation_type);
    if (MODAL_ACTION_TYPES.includes(template.operation_type)) {
      openModalFor(template.operation_type, template.parameters);
    } else {
      setConfirming(true);
    }
  }

  return (
    <div className={styles.bar}>
      <span className={styles.counter}>{selection.count} selected</span>
      <select id="batch-action-bar-action" name="action" value={action} onChange={(e) => setAction(e.target.value)} className={styles.select}>
        {actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
      </select>
      <Button
        size="sm"
        disabled={!canBatch && !single}
        onClick={() => (MODAL_ACTION_TYPES.includes(action) ? openModalFor(action) : setConfirming(true))}
      >
        Apply
      </Button>

      {/* Say why up front rather than letting the request come back 403.
          The single-hive routes above cover one selection, so this is the
          only case that genuinely needs the feature. */}
      {!canBatch && !single && (
        <span className={styles.counter}>
          Select one hive, or switch to the Advanced theme to act on several at once.
        </span>
      )}

      {canBatch && <Button size="sm" variant="ghost" onClick={() => setSaveOpen(true)}>Save as Template</Button>}

      {templates.length > 0 && (
        <select
          id="batch-action-bar-template"
          name="template"
          defaultValue=""
          onChange={(e) => {
            applyTemplate(e.target.value);
            e.target.value = "";
          }}
          className={styles.select}
          aria-label="Apply a saved batch template"
        >
          <option value="" disabled>Apply template…</option>
          {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
        </select>
      )}

      <Button size="sm" variant="ghost" onClick={selection.clear}>Clear</Button>

      {progress && (
        <div className={styles.progress}>
          <ProgressBar value={progress.done} max={progress.total} label={`${progress.done}/${progress.total}`} />
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={run}
        title="Confirm batch action"
        confirmLabel="Apply"
      >
        <p>This will affect {selection.count} hive(s). Continue?</p>
        {/* WHY, for an archive (#428). In the confirm step rather than the
            bar: the bar is a one-tap strip and a form in it would slow
            every other action down for the sake of one.

            ONE REASON FOR THE WHOLE SELECTION. Somebody archiving eleven
            hives at once is doing it because the same thing happened to
            all of them; asking per hive turns a batch operation back into
            eleven single ones, which is what the batch exists to avoid.

            Optional, like the single path. */}
        {action === "archive" ? (
          <SelectOrAdd
            listKey="archive_reason"
            label="Why? (optional)"
            value={archiveReason}
            onChange={setArchiveReason}
            placeholder="Not saying"
          />
        ) : null}
      </ConfirmDialog>

      <Modal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title="Save Batch Template"
        footer={<Button onClick={saveTemplate}>Save</Button>}
      >
        <label>
          Template name
          <input id="batch-action-bar-template-name" name="template-name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="touch-target"
            style={{ width: "100%" }}
            placeholder="e.g. Spring mite check"
          />
        </label>
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Saves "{actions.find((a) => a.value === action)?.label}" as a reusable template. Applying it later still asks you to
          pick which hives it runs against.
        </p>
      </Modal>

      {/* Assigning is not a batch OPERATION -- it writes one task per
          hive rather than one change across many -- so it gets its own
          dialog and its own gate (#288). */}
      <TeamAssignDialog
        open={teamAssignOpen}
        onClose={() => setTeamAssignOpen(false)}
        hiveIds={selection.ids}
        onAssigned={handleDialogApplied}
      />
      <BatchTreatmentDialog
        open={treatmentOpen}
        onClose={() => setTreatmentOpen(false)}
        hiveIds={selection.ids}
        initialParams={pendingParams}
        onApplied={handleDialogApplied}
      />
      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        hiveIds={selection.ids}
        initialParams={pendingParams}
        onApplied={handleDialogApplied}
      />
      <BatchSeasonDialog
        open={seasonOpen}
        onClose={() => setSeasonOpen(false)}
        hiveIds={selection.ids}
        onApplied={handleDialogApplied}
      />
      <BulkFeedingDialog
        open={bulkFeedingOpen}
        onClose={() => setBulkFeedingOpen(false)}
        hiveIds={selection.ids}
        initialParams={pendingParams}
        onApplied={handleDialogApplied}
      />
    </div>
  );
}
